import React from 'react';
import PartnerPageTemplate from './PartnerPageTemplate';
import {
  FileCheck,
  Repeat,
  ClipboardList,
  ShieldCheck,
  Users,
  MapPin,
  Phone,
  Route,
  Clock,
  HeartPulse,
} from 'lucide-react';

const CANONICAL = 'https://www.moveracaremobility.com/healthcare-partners/nemt-brokers';

const NEMTBrokers = () => {
  const seo = {
    title: 'NEMT Broker & Network Transportation Provider | Greater Houston | MoveraCare',
    description: 'Reliable NEMT transportation capacity for brokers, transportation coordinators, and network managers assigning non-emergency medical trips across Greater Houston.',
    canonical: CANONICAL,
    schemaId: 'partner-nemt-brokers-schema',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'NEMT Broker & Network Transportation Provider',
      serviceType: 'Non-Emergency Medical Transportation',
      areaServed: 'Greater Houston, TX',
      provider: {
        '@type': 'MedicalBusiness',
        name: 'MoveraCare Mobility',
        telephone: '+1-832-925-2128',
        url: 'https://www.moveracaremobility.com',
      },
      audience: {
        '@type': 'BusinessAudience',
        audienceType: 'NEMT Brokers, Transportation Networks, Trip Coordinators',
      },
    },
  };

  return (
    <PartnerPageTemplate
      testid="partner-nemt-brokers"
      seo={seo}
      eyebrow="NEMT BROKERS & NETWORKS"
      heading="Transportation Capacity for NEMT Brokers &amp; Network Managers"
      intro="MoveraCare Mobility supports NEMT brokers, transportation coordinators, and network organizations that assign non-emergency medical transportation trips across Greater Houston — offering reliable capacity, clear operational communication, and trip-level documentation."
      audience={{
        title: 'For NEMT Brokers, Networks & Transportation Coordinators',
        description: 'This page is designed for organizations managing NEMT networks and trip assignments — including transportation brokers, MCO subcontractors, network administrators, and dispatch coordinators.',
        bullets: [
          'NEMT brokerages assigning trips to network providers',
          'MCO and Medicaid transportation coordinators',
          'Regional NEMT network managers',
          'Transportation coordinators managing multi-provider networks',
          'Trip assignment and dispatch teams',
          'Operational quality and provider oversight teams',
        ],
      }}
      problem={{
        title: 'Predictable Capacity Where It’s Needed',
        description: 'Networks depend on providers that can accept, complete, and document assigned trips. MoveraCare offers dependable non-emergency transportation capacity in Fort Bend County and the broader Greater Houston service area, with communication that supports network operations.',
        bullets: [
          'Wheelchair and ambulatory trip acceptance across the Greater Houston service area',
          'Recurring trip capacity for dialysis, therapy, and standing authorizations',
          'On-demand acceptance windows for same-day and unplanned trips',
          'Documentation supporting network trip verification and billing',
          'Operational communication with dispatch and trip coordination teams',
        ],
      }}
      capabilities={{
        title: 'Capabilities That Support Network Trip Assignments',
        items: [
          { icon: Route, title: 'Trip Acceptance & Fulfillment', desc: 'Reliable acceptance and fulfillment of assigned non-emergency medical transportation trips.' },
          { icon: Repeat, title: 'Recurring Trip Capacity', desc: 'Standing capacity for recurring runs — dialysis, therapy, rehabilitation, and other authorizations.' },
          { icon: Clock, title: 'Same-Day / Unplanned Trip Support', desc: 'Same-day acceptance windows for unplanned or discharge-driven assignments where capacity allows.' },
          { icon: FileCheck, title: 'Trip Documentation', desc: 'Trip records to support network verification, completion tracking, and downstream billing.' },
          { icon: HeartPulse, title: 'Wheelchair & Ambulatory Options', desc: 'Wheelchair-accessible vans and comfortable ambulatory transport; drivers assist passengers during transport.' },
          { icon: ClipboardList, title: 'Dispatch Communication', desc: 'Clear operational communication with network dispatch and coordinator teams.' },
        ],
      }}
      coordination={{
        title: 'From Trip Assignment to Verified Completion',
        steps: [
          { title: 'Trip Assigned', desc: 'Broker or network coordinator assigns a non-emergency transportation trip to MoveraCare.' },
          { title: 'Capacity Confirmed', desc: 'MoveraCare confirms vehicle type, pickup window, and any special assistance needs.' },
          { title: 'Trip Completed', desc: 'Wheelchair or ambulatory driver completes the authorized trip with dispatch communication.' },
          { title: 'Documentation Provided', desc: 'Trip completion documentation is available to support network verification and billing.' },
        ],
      }}
      why={{
        title: 'Why Brokers & Networks Consider MoveraCare',
        items: [
          { icon: MapPin, title: 'Fort Bend County Core Coverage', desc: 'Concentrated capacity in Fort Bend County with reach across Greater Houston counties.' },
          { icon: Repeat, title: 'Recurring Schedule Reliability', desc: 'Consistent capacity for standing runs, minimizing missed trips and network gaps.' },
          { icon: FileCheck, title: 'Credentialing-Ready Documentation', desc: 'Vendor packet, insurance, and licensing information prepared for network onboarding.' },
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport for wheelchair and ambulatory trips.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious Operations', desc: 'Professional handling of member information across dispatch and operations.' },
          { icon: Phone, title: 'Direct Operational Contact', desc: 'Direct dispatch line and provider relations contact for network coordination.' },
        ],
      }}
      transportRequest={{
        title: 'Assign a Trip or Request Capacity',
        description: 'Network coordinators can send trip assignments and capacity requests through MoveraCare dispatch. New brokers can begin with a capacity conversation and a provider profile review.',
        cta: { label: 'Request Patient Transportation', to: '/request-patient-transportation' },
      }}
      vendorRelationship={{
        title: 'Onboard MoveraCare as a Network Provider',
        description: 'Network onboarding, credentialing, and provider relations teams can download MoveraCare’s vendor procurement packet or contact Provider Relations to begin network onboarding.',
      }}
    />
  );
};

export default NEMTBrokers;
